const SNAPSHOT_MARKER = "-snapshot-"; // no itx
const AUTOBACKUP_MARKER = "-snapshot-autobackup-"; // no itx

function timestamp(metadata: any): number {
  const localCopied = metadata?.localCopied ? new Date(metadata.localCopied).getTime() : NaN;
  if (Number.isFinite(localCopied)) return localCopied;

  const marker = metadata?.id?.includes(AUTOBACKUP_MARKER) ? AUTOBACKUP_MARKER : SNAPSHOT_MARKER;
  const markerPosition = metadata?.id?.indexOf(marker) ?? -1;
  if (markerPosition < 0) return 0;

  const idTimestamp = new Date(metadata.id.substring(markerPosition + marker.length)).getTime();
  return Number.isFinite(idTimestamp) ? idTimestamp : 0;
}

function belongsToProject(metadata: any, projectId: string): boolean {
  return metadata?.mainId === projectId || metadata?.id?.startsWith(`${projectId}${SNAPSHOT_MARKER}`);
}

export function isAutobackup(metadata: any): boolean {
  return metadata?.id?.includes(AUTOBACKUP_MARKER) === true;
}

export function isManualSnapshot(metadata: any): boolean {
  return metadata?.id?.includes(SNAPSHOT_MARKER) === true && !isAutobackup(metadata);
}

export function activeAutobackupIdsToDelete(projects: any[], projectId: string): string[] {
  const latestSnapshotTime = projects
    .filter((metadata) => belongsToProject(metadata, projectId) && isManualSnapshot(metadata))
    .reduce((latest, metadata) => Math.max(latest, timestamp(metadata)), Number.NEGATIVE_INFINITY);

  return projects
    .filter((metadata) => belongsToProject(metadata, projectId) && isAutobackup(metadata))
    .filter((metadata) => timestamp(metadata) > latestSnapshotTime)
    .map((metadata) => metadata.id);
}

function localDayKey(value: number): string {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function localMonthKey(value: number): string {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}`;
}

function localHourKey(value: number): string {
  const date = new Date(value);
  return `${localDayKey(value)}-${date.getHours()}`;
}

export function autobackupIdsToDelete(projects: any[], projectId: string, now = new Date()): string[] {
  const backups = projects
    .filter((metadata) => belongsToProject(metadata, projectId) && isAutobackup(metadata))
    .sort((left, right) => timestamp(right) - timestamp(left));

  const deleteIds = new Set<string>();
  const tenMinutesAgo = now.getTime() - 10 * 60 * 1000;
  const oneHourAgo = now.getTime() - 60 * 60 * 1000;

  let keptRecentBackup = false;
  const keptHours = new Set<string>();

  backups.forEach((backup) => {
    const backupTime = timestamp(backup);
    if (backupTime >= tenMinutesAgo) {
      if (keptRecentBackup) deleteIds.add(backup.id);
      keptRecentBackup = true;
    } else if (backupTime < oneHourAgo) {
      const hour = localHourKey(backupTime);
      if (keptHours.has(hour)) deleteIds.add(backup.id);
      keptHours.add(hour);
    }
  });

  return [...deleteIds];
}

export function idsToDeleteAfterManualSnapshot(
  projects: any[],
  projectId: string,
  now = new Date(),
): string[] {
  const deleteIds = new Set<string>(autobackupIdsToDelete(projects, projectId, now));
  const manualSnapshots = projects
    .filter((metadata) => belongsToProject(metadata, projectId) && isManualSnapshot(metadata))
    .sort((left, right) => timestamp(right) - timestamp(left));

  // The first entry is the snapshot that has just been saved. Remove all
  // autobackups encountered before the preceding snapshot in descending time
  // order. If this is the first snapshot, all older autobackups are obsolete.
  const precedingSnapshotTime = manualSnapshots.length > 1 ? timestamp(manualSnapshots[1]) : Number.NEGATIVE_INFINITY;
  projects
    .filter((metadata) => belongsToProject(metadata, projectId) && isAutobackup(metadata))
    .filter((metadata) => timestamp(metadata) > precedingSnapshotTime && timestamp(metadata) <= now.getTime())
    .forEach((metadata) => deleteIds.add(metadata.id));

  const today = localDayKey(now.getTime());
  const currentMonth = localMonthKey(now.getTime());
  const keptDays = new Set<string>();
  const keptMonths = new Set<string>();

  manualSnapshots.forEach((snapshot) => {
    const snapshotTime = timestamp(snapshot);
    const day = localDayKey(snapshotTime);
    if (day === today) return;

    const month = localMonthKey(snapshotTime);
    if (month === currentMonth) {
      if (keptDays.has(day)) deleteIds.add(snapshot.id);
      keptDays.add(day);
      return;
    }

    if (keptMonths.has(month)) deleteIds.add(snapshot.id);
    keptMonths.add(month);
  });

  return [...deleteIds];
}
